import { createContext, useContext, useMemo, useCallback } from 'react';
import { FundingSDK } from '@chainflip/sdk/funding';
import { assert } from '@chainflip/utils/assertion';
import { useQuery } from '@tanstack/react-query';
import { BrowserProvider, JsonRpcSigner } from 'ethers';
import type { Account, Chain, Client, Transport } from 'viem';
import { type Config, useConnectorClient } from 'wagmi';
import { getChainflipNetwork, TokenAmount } from '@/shared/utils';
import { flip$ } from '../assets/tokens';

function clientToSigner(client: Client<Transport, Chain, Account>) {
  const { account, chain, transport } = client;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new BrowserProvider(transport, network);
  const signer = new JsonRpcSigner(provider, account.address);
  return signer;
}

// https://wagmi.sh/react/guides/ethers#usage-1
/** @deprecated use wagmi + viem instead */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
  const { data: client } = useConnectorClient<Config>({ chainId });
  return useMemo(() => (client && client.chain ? clientToSigner(client) : undefined), [client]);
}

type FundingSDKContextProps = {
  fundingSDK: FundingSDK | undefined;
  flipBalance: string | undefined;
  approveFlip: (fundingAmount: bigint) => Promise<boolean>;
  fundStateChainAccount: (
    validatorIdHex: `0x${string}`,
    fundingAmount: bigint,
  ) => Promise<string | null>;
  minimumFunding: TokenAmount | undefined;
  redemptionDelayInMilliseconds: number | undefined;
};
interface FundingSDKProviderProps {
  children: React.ReactNode;
}
const FundingSDKContext = createContext<FundingSDKContextProps | undefined>(undefined);

/** @deprecated use wagmi + viem instead */
export const FundingSDKProvider = ({ children }: FundingSDKProviderProps): JSX.Element => {
  const signer = useEthersSigner();

  const { data: fundingSDK } = useQuery({
    queryKey: ['fundingSDK', signer?.address],
    queryFn: async () => {
      if (!signer) return null;

      const network = getChainflipNetwork();
      assert(network !== 'unknown', 'funding sdk does not support unknown chainflip network');

      return new FundingSDK({ signer, network });
    },
  });

  const { data: flipBalance, refetch: updateFlipBalance } = useQuery({
    queryKey: ['flipBalance', signer?.address, Boolean(fundingSDK)],
    queryFn: () => {
      if (!fundingSDK) return null;
      return fundingSDK.getFlipBalance();
    },
    select: (balance) => {
      if (balance === null) return null;
      return balance.toString();
    },
    retry: true,
    retryDelay: 100,
    refetchInterval: 12000,
  });

  const { data: redemptionDelayInMilliseconds } = useQuery({
    queryKey: ['redemptionDelay', Boolean(fundingSDK)],
    queryFn: () => {
      if (!fundingSDK) return null;
      return fundingSDK.getRedemptionDelay();
    },
    select: (delay) => {
      if (delay === null) return null;
      return Number(delay * 1000n);
    },
    retry: true,
    retryDelay: 100,
    refetchInterval: 12000,
  });

  const { data: minimumFunding } = useQuery({
    queryKey: ['minimumFunding', Boolean(fundingSDK)],
    queryFn: async () => {
      if (!fundingSDK) return null;
      return fundingSDK.getMinimumFunding();
    },
    select: (amount) => {
      if (amount === null) return null;
      return new TokenAmount(amount, flip$.decimals);
    },
  });

  const approveFlip = useCallback(
    async (fundingAmount: bigint): Promise<boolean> => {
      if (!fundingSDK) return false;
      await fundingSDK.approveStateChainGateway(fundingAmount);

      return true;
    },
    [fundingSDK],
  );

  const fundStateChainAccount = useCallback(
    async (validatorIdHex: `0x${string}`, fundingAmount: bigint): Promise<string | null> => {
      if (!fundingSDK) return null;
      const txHash = await fundingSDK.fundStateChainAccount(validatorIdHex, fundingAmount);
      await updateFlipBalance();

      return txHash;
    },
    [fundingSDK, updateFlipBalance],
  );

  const context = useMemo(
    () => ({
      fundingSDK: fundingSDK ?? undefined,
      flipBalance: flipBalance ?? undefined,
      fundStateChainAccount,
      approveFlip,
      minimumFunding: minimumFunding ?? undefined,
      redemptionDelayInMilliseconds: redemptionDelayInMilliseconds ?? undefined,
    }),
    [
      fundingSDK,
      flipBalance,
      fundStateChainAccount,
      approveFlip,
      minimumFunding,
      redemptionDelayInMilliseconds,
    ],
  );

  return <FundingSDKContext.Provider value={context}>{children}</FundingSDKContext.Provider>;
};

/** @deprecated use wagmi + viem instead */
const useOldFundingSDK = (): FundingSDKContextProps => {
  const context = useContext(FundingSDKContext);
  if (context === undefined) {
    throw new Error('useOldFundingSDK must be used within the FundingSDKProvider');
  }
  return context;
};

export default useOldFundingSDK;
